import React, { useState, useEffect } from "react";
import { InputNumber } from "primereact/inputnumber";
import { Button } from "primereact/button";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Dropdown } from "primereact/dropdown";
import { makeRequest2 } from "../../../../../../utils/makeRequest";
import { toastify } from "../../../../../../utils/toast";
import { useFacilityStore } from "../../../../../../app/z-store/store";

const ChargesTab = () => {
  const selectedFacilityId = useFacilityStore((state) => state.facilityId);

  // Power Charges State
  const [powerCharges, setPowerCharges] = useState([]);
  const [powerChargeForm, setPowerChargeForm] = useState({
    yearMonth: '',
    tariff: '',
    assumedConsumedUnit: 0,
    ec: 0,
    fcc: 0,
    ferfa: 0,
    ia: 0,
    warma: 0,
    erc: 0,
    repPercentage: 0,
    pfs: 0,
    vatPercentage: 16,
    totalCharge: 0
  });
  const [editingChargeId, setEditingChargeId] = useState(null);
  const [showChargeForm, setShowChargeForm] = useState(false);
  const [rows, setRows] = useState(10);
  const [loading, setLoading] = useState(false);

  // Tariff options
  const tariffOptions = [
    { label: 'Domestic Lifeline', value: 'Domestic Lifeline' },
    { label: 'Domestic Ordinary 1', value: 'Domestic Ordinary 1' },
    { label: 'Domestic Ordinary 2', value: 'Domestic Ordinary 2' },
    { label: 'Commercial Low Voltage', value: 'Commercial Low Voltage' },
    { label: 'Commercial Medium Voltage', value: 'Commercial Medium Voltage' },
    { label: 'Industrial Low Voltage', value: 'Industrial Low Voltage' },
    { label: 'Industrial Medium Voltage', value: 'Industrial Medium Voltage' },
    { label: 'Industrial High Voltage', value: 'Industrial High Voltage' }
  ];

  // Pagination options
  const rowsPerPageOptions = [10, 20, 50, 100];

  // Load power charges when selectedFacilityId changes
  useEffect(() => {
    if (selectedFacilityId) {
      fetchPowerCharges();
    }
  }, [selectedFacilityId]);

  // Auto-calculate total charge when individual charges change
  useEffect(() => {
    const baseCharges = (powerChargeForm.ec || 0) +
      (powerChargeForm.fcc || 0) +
      (powerChargeForm.ferfa || 0) +
      (powerChargeForm.ia || 0) +
      (powerChargeForm.warma || 0) +
      (powerChargeForm.erc || 0) +
      (powerChargeForm.pfs || 0);

    const repAmount = baseCharges * (powerChargeForm.repPercentage || 0) / 100;
    const subtotal = baseCharges + repAmount;
    const vatAmount = subtotal * (powerChargeForm.vatPercentage || 0) / 100;
    const total = subtotal + vatAmount;

    setPowerChargeForm(prev => ({ ...prev, totalCharge: total }));
  }, [
    powerChargeForm.ec,
    powerChargeForm.fcc,
    powerChargeForm.ferfa,
    powerChargeForm.ia,
    powerChargeForm.warma,
    powerChargeForm.erc,
    powerChargeForm.pfs,
    powerChargeForm.repPercentage,
    powerChargeForm.vatPercentage
  ]);

  // Fetch Power Charges
  const fetchPowerCharges = async () => {
    try {
      setLoading(true);

      if (!selectedFacilityId) {
        toastify('No facility ID available', 'error');
        setLoading(false);
        return;
      }

      const response = await makeRequest2(
        `/api/app/power_meter_management/controllers/power_charges/get_all/${selectedFacilityId}`,
        "GET"
      );

      if (response && response.data) {
        let powerChargesData = [];

        if (Array.isArray(response.data)) {
          powerChargesData = response.data;
        } else if (response.data.data && Array.isArray(response.data.data)) {
          powerChargesData = response.data.data;
        } else if (response.data.powerCharges && Array.isArray(response.data.powerCharges)) {
          powerChargesData = response.data.powerCharges;
        }

        setPowerCharges(powerChargesData);
      } else {
        toastify(response?.error || 'Failed to fetch power charges', 'error');
        setPowerCharges([]);
      }
    } catch (error) {
      console.error('Error fetching power charges:', error);
      toastify('An error occurred while fetching power charges', 'error');
      setPowerCharges([]);
    } finally {
      setLoading(false);
    }
  };

  // Power Charge Management
  const handleAddPowerCharge = () => {
    setShowChargeForm(true);
    setEditingChargeId(null);
    setPowerChargeForm({
      yearMonth: '',
      tariff: '',
      assumedConsumedUnit: 0,
      ec: 0,
      fcc: 0,
      ferfa: 0,
      ia: 0,
      warma: 0,
      erc: 0,
      repPercentage: 5,
      pfs: 0,
      vatPercentage: 16,
      totalCharge: 0
    });
  };

  const handleEditPowerCharge = (charge) => {
    setShowChargeForm(true);
    setEditingChargeId(charge._id);
    setPowerChargeForm({
      yearMonth: charge.yearMonth,
      tariff: charge.tariff,
      assumedConsumedUnit: charge.assumedConsumedUnit,
      ec: charge.energyCharge || 0,
      fcc: charge.fuelCostCharge || 0,
      ferfa: charge.forexAdjustment || 0,
      ia: charge.inflationAdjustment || 0,
      warma: charge.waterResourceManagementLevy || 0,
      erc: charge.energyRegulatoryLevy || 0,
      repPercentage: charge.repPercentage || charge.ruralElectrificationLevy || 0,
      pfs: charge.powerFactorSurcharge || 0,
      vatPercentage: charge.vatPercentage || (charge.valueAddedTax * 100) || 16,
      totalCharge: charge.totalCharge
    });
  };

  const handleSavePowerCharge = async () => {
    try {
      if (!powerChargeForm.yearMonth) {
        toastify("Please select a year and month", "error");
        return;
      }

      if (!powerChargeForm.tariff) {
        toastify("Please select a tariff", "error");
        return;
      }

      if (!selectedFacilityId) {
        toastify('No facility ID available', 'error');
        return;
      }

      const chargeData = {
        ...powerChargeForm,
        facilityId: selectedFacilityId
      };

      let response;
      if (editingChargeId) {
        response = await makeRequest2(
          `/api/app/power_meter_management/controllers/power_charges/edit_charge/${editingChargeId}`,
          "PUT",
          chargeData
        );
      } else {
        response = await makeRequest2(
          `/api/app/power_meter_management/controllers/power_charges/add_charge`,
          "POST",
          chargeData
        );
      }

      if (response && (response.success || response.data)) {
        toastify(`Power charge ${editingChargeId ? 'updated' : 'added'} successfully`, "success");
        setShowChargeForm(false);
        fetchPowerCharges();
      } else {
        toastify(response?.error || response?.message || "Failed to save power charge", "error");
      }
    } catch (error) {
      console.error('Error saving power charge:', error);
      toastify("Error saving power charge", "error");
    }
  };

  const handleDeletePowerCharge = async (chargeId) => {
    if (window.confirm("Are you sure you want to delete this power charge?")) {
      try {
        toastify("Delete functionality not yet implemented", "info");
      } catch (error) {
        console.error('Error deleting power charge:', error);
        toastify("Error deleting power charge", "error");
      }
    }
  };

  const formatYearMonth = (yearMonth) => {
    if (!yearMonth) return '';
    const [year, month] = yearMonth.split('-');
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
      'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${year}-${month.padStart(2, '0')}`;
  };

  // DataTable column templates
  const periodBodyTemplate = (rowData) => {
    return <strong>{formatYearMonth(rowData.yearMonth)}</strong>;
  };

  const tariffBodyTemplate = (rowData) => {
    return <span className="text-primary">{rowData.tariff}</span>;
  };

  const numberBodyTemplate = (rowData, field) => {
    return rowData[field]?.toLocaleString() || '0';
  };

  const currencyBodyTemplate = (rowData, field) => {
    let value = 0;
    switch (field) {
      case 'ec':
        value = rowData.energyCharge || rowData[field];
        break;
      case 'fcc':
        value = rowData.fuelCostCharge || rowData[field];
        break;
      case 'ferfa':
        value = rowData.forexAdjustment || rowData[field];
        break;
      case 'ia':
        value = rowData.inflationAdjustment || rowData[field];
        break;
      case 'warma':
        value = rowData.waterResourceManagementLevy || rowData[field];
        break;
      case 'erc':
        value = rowData.energyRegulatoryLevy || rowData[field];
        break;
      case 'pfs':
        value = rowData.powerFactorSurcharge || rowData[field];
        break;
      default:
        value = rowData[field];
    }
    return value?.toLocaleString() || '0';
  };

  const percentageBodyTemplate = (rowData, field) => {
    return `${rowData[field] || 0}%`;
  };

  const totalChargeBodyTemplate = (rowData) => {
    return (
      <strong className="text-success">
        {rowData.totalCharge?.toLocaleString() || '0'}
      </strong>
    );
  };

  const actionsBodyTemplate = (rowData) => {
    return (
      <div className="btn-group">
        <Button
          icon="ti ti-edit"
          className="p-button-rounded p-button-info p-button-sm"
          onClick={() => handleEditPowerCharge(rowData)}
          tooltip="Edit charge"
          tooltipOptions={{ position: 'top' }}
        />
        <Button
          icon="pi pi-trash"
          className="p-button-rounded p-button-danger p-button-sm ml-2"
          onClick={() => handleDeletePowerCharge(rowData._id)}
          tooltip="Delete charge"
          tooltipOptions={{ position: 'top' }}
        />
      </div>
    );
  };

  return (
    <div className="card">
      <div className="card-header">
        <h4 className="card-title mb-0">KPLC Power Charges</h4>
      </div>
      <div className="card-body">
        <div className="charges-container">
          <div className="row mb-4">
            <div className="col-12">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <Button
                  label="Add Power Charge"
                  className="btn btn-primary btn-sm"
                  onClick={handleAddPowerCharge}
                />
              </div>

              {/* Power Charges DataTable */}
              <DataTable
                value={powerCharges}
                loading={loading}
                paginator
                rows={rows}
                rowsPerPageOptions={rowsPerPageOptions}
                stripedRows
                className="p-datatable-striped"
                emptyMessage="No power charges configured yet"
                responsiveLayout="scroll"
                size="small"
              >
                <Column
                  field="yearMonth"
                  header="Year-Month"
                  body={periodBodyTemplate}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column
                  field="tariff"
                  header="Tariff"
                  body={tariffBodyTemplate}
                  sortable
                  style={{ width: '150px' }}
                />
                <Column
                  field="assumedConsumedUnit"
                  header="Assumed Consumed Unit"
                  body={(rowData) => numberBodyTemplate(rowData, 'assumedConsumedUnit')}
                  sortable
                  style={{ width: '120px' }}
                />
                <Column
                  field="ec"
                  header="EC"
                  body={(rowData) => currencyBodyTemplate(rowData, 'ec')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="fcc"
                  header="FCC"
                  body={(rowData) => currencyBodyTemplate(rowData, 'fcc')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="ferfa"
                  header="FERFA"
                  body={(rowData) => currencyBodyTemplate(rowData, 'ferfa')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="ia"
                  header="IA"
                  body={(rowData) => currencyBodyTemplate(rowData, 'ia')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="warma"
                  header="WARMA"
                  body={(rowData) => currencyBodyTemplate(rowData, 'warma')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="erc"
                  header="ERC"
                  body={(rowData) => currencyBodyTemplate(rowData, 'erc')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="repPercentage"
                  header="REP %"
                  body={(rowData) => percentageBodyTemplate(rowData, 'repPercentage')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="pfs"
                  header="PFS"
                  body={(rowData) => currencyBodyTemplate(rowData, 'pfs')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  field="vatPercentage"
                  header="VAT %"
                  body={(rowData) => percentageBodyTemplate(rowData, 'vatPercentage')}
                  sortable
                  style={{ width: '80px' }}
                />
                <Column
                  header="Actions"
                  body={actionsBodyTemplate}
                  style={{ width: '120px' }}
                />
              </DataTable>

              {/* Power Charge Form Modal */}
              {showChargeForm && (
                <div className="modal fade show" style={{ display: 'block', backgroundColor: 'rgba(0,0,0,0.5)' }}>
                  <div className="modal-dialog modal-xl">
                    <div className="modal-content">
                      <div className="modal-header">
                        <h5 className="modal-title">
                          {editingChargeId ? 'Edit Power Charge' : 'Add Power Charge'}
                        </h5>
                        <button
                          type="button"
                          className="btn-close"
                          onClick={() => setShowChargeForm(false)}
                        ></button>
                      </div>
                      <div className="modal-body">
                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold">Year & Month</label>
                            <input
                              type="month"
                              className="form-control"
                              value={powerChargeForm.yearMonth}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                yearMonth: e.target.value
                              })}
                            />
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold">Tariff</label>
                            <select
                              className="form-control"
                              value={powerChargeForm.tariff}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                tariff: e.target.value
                              })}
                            >
                              <option value="">Select Tariff</option>
                              {tariffOptions.map((option, index) => (
                                <option key={index} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="col-md-4 mb-3">
                            <label className="form-label fw-bold">Assumed Consumed Unit</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.assumedConsumedUnit}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                assumedConsumedUnit: e.value
                              })}
                              min={0}
                              maxFractionDigits={2}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">EC</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.ec}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                ec: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">FCC</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.fcc}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                fcc: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">FERFA</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.ferfa}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                ferfa: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">IA</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.ia}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                ia: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">WARMA</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.warma}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                warma: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">ERC</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.erc}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                erc: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">REP %</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.repPercentage}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                repPercentage: e.value
                              })}
                              min={0}
                              max={100}
                              suffix="%"
                              maxFractionDigits={2}
                            />
                          </div>
                          <div className="col-md-3 mb-3">
                            <label className="form-label fw-bold">PFS</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.pfs}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                pfs: e.value
                              })}
                              min={0}
                              maxFractionDigits={4}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold">VAT %</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.vatPercentage}
                              onChange={(e) => setPowerChargeForm({
                                ...powerChargeForm,
                                vatPercentage: e.value
                              })}
                              min={0}
                              max={100}
                              suffix="%"
                              maxFractionDigits={2}
                            />
                          </div>
                          <div className="col-md-6 mb-3">
                            <label className="form-label fw-bold">Total Charge</label>
                            <InputNumber
                              className="w-100"
                              value={powerChargeForm.totalCharge}
                              disabled
                              maxFractionDigits={2}
                            />
                            <small className="text-muted">
                              <i className="pi pi-info-circle me-1"></i>
                              Auto-calculated from individual charges
                            </small>
                          </div>
                        </div>
                      </div>
                      <div className="modal-footer">
                        <Button
                          label="Cancel"
                          className="btn btn-secondary"
                          onClick={() => setShowChargeForm(false)}
                        />
                        <Button
                          label={editingChargeId ? 'Update' : 'Add'}
                          className="btn btn-primary"
                          onClick={handleSavePowerCharge}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ChargesTab;